import React, { useEffect, useState } from 'react'
import { Button } from '@mui/material'
import { useLocation, useNavigate } from "@reach/router"
import userStore from '../common/userStore'
import useTitle from '../../hooks/title'
import useSchema from '../../hooks/schema'
import Header from '../header/Header'
import AuthHeader from '../layout/AuthHeader'
import SaveAltIcon from '@mui/icons-material/SaveAlt'
import {ReactComponent as AffiliateBlack} from '../../images/affiliate-black.svg'
import {ReactComponent as Affiliate} from '../../images/affiliate.svg'
import {ReactComponent as DiscordIcon} from '../../images/discord-white.svg'
import {ReactComponent as Windows} from '../../images/windows.svg'
import {ReactComponent as Mac} from '../../images/mac.svg'
import Footer from '../footer/FooterBlack'
import classes from './PitchNet.module.scss'
import clsx from 'clsx'
import {createApi} from "../common/API"
import layoutStyles from '../layout/Layout.module.scss'
import pitchnet1 from '../../images/pc/pitchnet1.mp4'
import pitchnet2 from '../../images/pc/pitchnet2.mp4'
import pitchnet3 from '../../images/pc/pitchnet3.mp4'
const showApp = createApi('/app/getApps', {method: 'POST', expandData: true, notify: true, noThrow: false});

const walkthroughItems = [
    {
        title: 'Resynthesize a natural performance',
        description: 'Instead of only warping the original waveform, PitchNet uses AI resynthesis to rebuild tuned vocals with smoother transitions and a more natural tone.',
        video: pitchnet1
    },
    {
        title: 'Edit vocals as musical notes',
        description: 'PitchNet detects vocal pitch and displays editable note regions, making intonation and melody changes clear without digging through raw waveforms.',
        video: pitchnet2
    },
    {
        title: 'Stay connected with ARA',
        description: 'ARA integration in compatible DAWs lets PitchNet follow your timeline, analyze audio faster and keep vocal tuning close to the arrangement.',
        video: pitchnet3
    }
];

const faqItems = [
    {
        question: 'What is PitchNet?',
        answer: 'PitchNet is a free AI vocal tuning plugin that uses AI resynthesis to rebuild tuned vocals with a natural tone, instead of only warping the original waveform.'
    },
    {
        question: 'Is PitchNet really free?',
        answer: 'Yes. PitchNet is free and open-source. You can download the installer for Windows or Mac and start tuning vocals inside your DAW at no cost.'
    },
    {
        question: 'Which plugin formats does PitchNet support?',
        answer: 'The Windows installer includes VST3 and AAX. The Mac installer includes VST3, AU and AAX, with a Universal Binary for Apple Silicon and Intel.'
    },
    {
        question: 'Does PitchNet support ARA?',
        answer: 'Yes. ARA integration in compatible DAWs lets PitchNet follow your timeline, analyze audio faster and keep vocal tuning closely connected to the arrangement.'
    },
    {
        question: 'Do I need a dedicated GPU to run PitchNet?',
        answer: 'A dedicated GPU is recommended since PitchNet uses AI resynthesis to process vocals, but it can still run on integrated graphics with reduced performance.'
    },
    {
        question: 'How is PitchNet different from traditional pitch correction plugins?',
        answer: 'Traditional pitch correction plugins warp the original waveform directly. PitchNet instead detects vocal pitch, displays editable note regions and uses AI resynthesis to rebuild the performance with smoother transitions.'
    },
    {
        question: 'Where can I get help or share feedback?',
        answer: 'Join the PitchNet Discord community to share feedback, follow updates and connect with other producers tuning vocals with PitchNet.'
    }
];

const PitchNet = () => {
    useTitle("PitchNet — Free AI Vocal Tuning Plugin", "Natural pitch correction plugin powered by AI resynthesis. Free vocal pitch correction with ARA integration for modern DAW workflows.", "/pitchnet")
    useSchema({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "SoftwareApplication",
          "name": "PitchNet",
          "description": "Free AI vocal tuning plugin using AI resynthesis for natural pitch correction and ARA-integrated DAW workflows.",
          "url": "https://sessionloops.com/pitchnet",
          "applicationCategory": "MusicApplication",
          "operatingSystem": "Windows 10 or later, macOS 10.15 or later",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD",
            "availability": "https://schema.org/InStock",
            "url": "https://sessionloops.com/pitchnet#downloads"
          },
          "publisher": {
            "@type": "Organization",
            "name": "Session Loops",
            "url": "https://sessionloops.com",
            "logo": "https://sessionloops.com/logo.png"
          }
        },
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://sessionloops.com/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "PitchNet",
              "item": "https://sessionloops.com/pitchnet"
            }
          ]
        },
        {
          "@type": "FAQPage",
          "mainEntity": faqItems.map(item => ({
            "@type": "Question",
            "name": item.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": item.answer
            }
          }))
        }
      ]
    })
    const [version, setVersion] = useState('');
    const [winUrl, setWinUrl] = useState('');
    const [macUrl, setMacUrl] = useState('');
    const [isAffiliateHover, setIsAffiliateHover] = useState(false);
    const [activeFaqIndex, setActiveFaqIndex] = useState(null);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        if (window.location.hash) {
            let dom = document.getElementById(window.location.hash.replace('#', ''));
            dom && dom.scrollIntoView({block:"center", behavior:"smooth"})
        } else {
            window.scrollTo(0, 0)
        }
        const fetchApiData = async () => {
            try {
              const response = await showApp();
              const app = response.find(item => item.appId === 'PitchNet' || item.name === 'PitchNet');
              if (app) {
                setVersion(app.version || '')
                setWinUrl(app.winUrl || '')
                setMacUrl(app.macUrl || '')
              }
            } catch (error) {
              console.error('Error fetching API data:', error);
            }
          };
        fetchApiData()
    }, [location.pathname])

    return <div className={clsx(classes.root, layoutStyles.root)}>
      {userStore.isAnonymous ? <Header /> : <AuthHeader />}
        <div className={classes.banner}>
            <div className={classes.heroCopy}>
                <h1>PitchNet</h1>
                <p>Natural Pitch Correction Plugin Powered by AI Resynthesis</p>
            </div>
        </div>
        <div className={classes.options} >
            <div className={clsx(classes.option, classes.white)}
                onClick = {() => document.getElementById("downloads").scrollIntoView({block:"center", behavior:"smooth"})}
            >
                <SaveAltIcon style={{fontSize: 28}}/>
                <div className={classes.optionText}>Download Free</div>
            </div>
            <a className={clsx(classes.option, classes.white)} style={{textDecoration: 'none'}} href="https://discord.gg/REEHXucYTb" target="_blank" rel="noreferrer"
                onMouseEnter={() => setIsAffiliateHover(true)}
                onMouseLeave={() => setIsAffiliateHover(false)}
            >
                {isAffiliateHover ? <AffiliateBlack style={{width: 27, height: 27}}/> : <Affiliate style={{width: 27, height: 27}}/>}
                <div className={classes.optionText}>Share Feedback</div>
            </a>
        </div>
        <main className={classes.content}>
            <section className={classes.whatSection}>
                <h2>What is PitchNet?</h2>
                <div className={classes.walkthroughList}>
                    {walkthroughItems.map((item) => (
                        <div className={classes.walkthrough} key={item.title}>
                            <div className={classes.whatVisual}>
                                <video className={classes.walkthroughVideo} autoPlay loop muted playsInline>
                                    <source src={item.video} type="video/mp4" />
                                </video>
                            </div>
                            <div className={classes.walkthroughText}>
                                <h3>{item.title}</h3>
                                <p>{item.description}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </section>
            <section id="downloads" className={classes.downloadPanel}>
                <div className={classes.downloadHero} aria-label="Get PitchNet for Free" />
                <p>PitchNet is free and open-source. Download the installer for your system and start tuning vocals inside your DAW.</p>
                <a
                    className={classes.sourceLink}
                    href="https://github.com/SessionLoops/PitchNet"
                    target="_blank"
                    rel="noreferrer"
                >
                    View source on GitHub →
                </a>
                <div className={classes.downloads}>
                    <div className={classes.download}>
                        <Button startIcon={<Windows style={{marginLeft: 7, marginRight: 10}}/>} variant="contained" className={classes.button} href={winUrl} target="_blank">Download for Windows</Button>
                        Version {version}, VST3 & AAX, Dedicated GPU Recommended
                    </div>
                    <div className={classes.download}>
                        <Button startIcon={<Mac style={{marginLeft: 7, marginRight: 10}}/>} variant="contained" className={classes.button} href={macUrl} target="_blank">Download for Mac</Button>
                        Version {version}, VST3 & AU & AAX, Universal
                    </div>
                </div>
            </section>
            <section className={classes.supportPanel}>
                <h2>Explore More from Session Loops</h2>
                <p>
                    PitchNet is completely free. Explore a Session Loops subscription to enjoy VocalNet,
                    DrumNet and our other products while supporting PitchNet&rsquo;s continued development.
                </p>
                <Button
                    variant="contained"
                    className={classes.supportButton}
                    onClick={() => navigate('/pricing')}
                >
                    View Subscription Plans
                </Button>
            </section>
            <section className={classes.discordPanel}>
                <h2>Join Our Community</h2>
                <p>Share feedback, follow updates and connect with other producers tuning vocals with PitchNet.</p>
                <Button
                    startIcon={<DiscordIcon style={{width: 28, height: 32, marginLeft: 7, marginRight: 10}}/>}
                    variant="contained"
                    className={classes.discordButton}
                    href="https://discord.gg/REEHXucYTb"
                    target="_blank"
                    rel="noreferrer"
                >
                    Join Our Discord
                </Button>
            </section>
            <section className={classes.requirementsPanel}>
                <h2>System Requirements</h2>
                <div className={classes.requirementsList}>
                    <div className={classes.requirement}>
                        <Windows style={{width: 30, height: 30}}/>
                        <h3>Windows</h3>
                        <ul>
                            <li>Windows 10 (64-bit) or later</li>
                            <li>VST3 or AAX compatible host</li>
                            <li>8GB RAM minimum, 16GB recommended</li>
                            <li>Dedicated GPU recommended</li>
                            <li>500MB free disk space</li>
                        </ul>
                    </div>
                    <div className={classes.requirement}>
                        <Mac style={{width: 30, height: 30}}/>
                        <h3>Mac</h3>
                        <ul>
                            <li>macOS 13 or later</li>
                            <li>VST3, AU or AAX compatible host</li>
                            <li>Apple Silicon or Intel, Universal Binary</li>
                            <li>8GB RAM minimum, 16GB recommended</li>
                            <li>500MB free disk space</li>
                        </ul>
                    </div>
                </div>
            </section>
            <section className={classes.faqSection}>
                <h2>Frequently Asked Questions</h2>
                <div className={classes.faqList}>
                    {faqItems.map((item, index) => (
                        <div key={item.question} className={classes.faqItem}>
                            <button
                                type="button"
                                className={classes.faqQuestion}
                                aria-expanded={activeFaqIndex === index}
                                aria-controls={`pitchnet-faq-${index}`}
                                onClick={() => setActiveFaqIndex(activeFaqIndex === index ? null : index)}
                            >
                                <span>{item.question}</span>
                                <span className={clsx(classes.faqIcon, activeFaqIndex === index && classes.faqIconOpen)} />
                            </button>
                            <div
                                id={`pitchnet-faq-${index}`}
                                className={clsx(classes.faqAnswer, activeFaqIndex === index && classes.faqAnswerOpen)}
                            >
                                <p>{item.answer}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </section>
        </main>
        <Footer />
      </div>
  }

  export default PitchNet
